import { createServiceWorkerClient, type ServiceWorkerClient } from '~/pwa/serviceWorkerClient'
import {
  INITIAL_UPDATE_STATE,
  mayActivateWaitingWorker,
  nextUpdateState,
  type UpdateEvent,
  type UpdateState,
} from '~/pwa/updateMachine'
import { err, ok, type Result } from '~/utils/result'

/**
 * Orchestrates the D-044 update lifecycle: it owns the machine state, listens
 * to the service worker, and is the single surface the UI and the weekly review
 * talk to.
 *
 * The machine itself is pure and lives in `~/pwa/updateMachine`. Everything
 * impure — the registration, the draft save, the reload — is here.
 */

export type ActivationFailure =
  /** The machine refused the request; an active review was in progress. */
  | { readonly kind: 'notOfferable' }
  /** The draft save did not resolve successfully. The update was not applied. */
  | { readonly kind: 'draftSaveFailed'; readonly reason: string }

/**
 * Force-saves in-progress review work. Returns a typed failure rather than
 * throwing, so a failed save can never be mistaken for a saved draft (D-056).
 */
export type DraftSaver = () => Promise<Result<void, string>>

type Listener = (state: UpdateState) => void

let state: UpdateState = INITIAL_UPDATE_STATE
let client: ServiceWorkerClient | null = null
let draftSaver: DraftSaver | null = null
const listeners = new Set<Listener>()

function dispatch(event: UpdateEvent): UpdateState {
  const previous = state
  state = nextUpdateState(previous, event)
  if (state !== previous) {
    // Copy first: a listener may unsubscribe while being notified.
    for (const listener of Array.from(listeners)) listener(state)
  }
  return state
}

export function getUpdateState(): UpdateState {
  return state
}

export function subscribeToUpdateState(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Begin listening for updates. Called once at boot, after the database is
 * usable. Safe to call when service workers are unavailable — the app simply
 * never has an update to offer.
 */
export function startUpdateWatch(
  createClient: typeof createServiceWorkerClient = createServiceWorkerClient,
  reload: () => void = () => {
    window.location.reload()
  },
): void {
  if (client) return

  client = createClient({
    onWaiting: () => {
      dispatch({ type: 'updateDetected' })
    },
    onControlling: () => {
      /* Two guards, and both matter.

         `applying` means this tab asked for the update. Another tab activating
         the waiting worker also fires `controlling` here, and reloading on that
         would be an automatic reload nobody asked for.

         `!reviewActive` closes the last window in D-044 rule 1. Between
         messaging the waiting worker and it taking control, the user can open
         the weekly review and start typing. The activation itself cannot be
         retracted at that point — but the reload can be withheld, and rule 1 is
         absolute: an active review never reloads automatically, for any reason,
         in any state. The new worker is in control either way; this tab simply
         keeps running the shell it already has until the user navigates. */
      if (state.status === 'applying' && !state.reviewActive) reload()
    },
  })
}

/**
 * Register the function that force-saves in-progress review work. The weekly
 * review calls this on mount and clears it on unmount.
 */
export function registerDraftSaver(saver: DraftSaver | null): void {
  draftSaver = saver
}

/* ---- Weekly-review signals (D-044) -------------------------------------- */

/** A weekly review has become active. Blocks the update action. */
export function markReviewStarted(): void {
  dispatch({ type: 'reviewStarted' })
}

/** The review was finalized. The update action may now be offered. */
export function markReviewFinalized(): void {
  dispatch({ type: 'reviewFinalized' })
}

/** The user explicitly left or abandoned the review. */
export function markReviewAbandoned(): void {
  dispatch({ type: 'reviewAbandoned' })
}

/**
 * Routine autosave happened. This is deliberately inert: a draft having been
 * persisted is NOT permission to activate an update (D-044 rule 3). It exists
 * so that the rule is covered by a test rather than by the absence of a call.
 */
export function noteDraftAutosaved(): void {
  dispatch({ type: 'draftAutosaved' })
}

/* ---- Activation --------------------------------------------------------- */

/**
 * Apply the waiting update. Only ever called from an explicit user action.
 *
 * Sequence, in this order and no other: ask the machine (which refuses while a
 * review is active) → force-save any draft → confirm the save resolved → only
 * then activate the waiting worker. A failed save aborts and surfaces the
 * failure; the update is not applied and the user keeps their work.
 */
export async function requestUpdateActivation(): Promise<Result<void, ActivationFailure>> {
  const requested = dispatch({ type: 'activationRequested' })
  if (requested.status !== 'activating') return err({ kind: 'notOfferable' })

  const saved = await saveDraft()
  if (!saved.ok) {
    dispatch({ type: 'activationSaveFailed', reason: saved.error })
    return err({ kind: 'draftSaveFailed', reason: saved.error })
  }

  const confirmed = dispatch({ type: 'activationSaveConfirmed' })
  if (!mayActivateWaitingWorker(confirmed)) return err({ kind: 'notOfferable' })

  client?.activate()
  return ok(undefined)
}

async function saveDraft(): Promise<Result<void, string>> {
  // No draft registered means there is nothing to lose.
  if (!draftSaver) return ok(undefined)
  try {
    return await draftSaver()
  } catch (cause: unknown) {
    return err(cause instanceof Error ? cause.message : String(cause))
  }
}

/** Test helper. Not used by application code. */
export function resetUpdateService(): void {
  state = INITIAL_UPDATE_STATE
  client?.dispose()
  client = null
  draftSaver = null
  listeners.clear()
}
